/**
 * O formato do que a Base de Erros Conhecidos devolve.
 *
 * "Erro Conhecido" e "Solução de Contorno" são os termos ITIL em português do
 * documento 09 §6 — não "known error" nem "workaround", e nem tradução literal
 * inventada na tela.
 *
 * Nada aqui é inventado: cada campo existe em alguma resposta de
 * `apps/api/src/itsm/kedb/kedb.controller.ts`.
 */

export const SITUACOES_DE_ERRO = ['ACTIVE', 'RESOLVED', 'RETIRED'] as const;
export type SituacaoDeErro = (typeof SITUACOES_DE_ERRO)[number];

export const ROTULO_DE_SITUACAO_DE_ERRO: Record<SituacaoDeErro, string> = {
  ACTIVE: 'Ativo',
  RESOLVED: 'Corrigido',
  RETIRED: 'Aposentado',
};

export const RESUMO_DE_SITUACAO_DE_ERRO: Record<SituacaoDeErro, string> = {
  ACTIVE: 'Continua acontecendo, e o contorno vale. É o único estado que a Central sugere no atendimento.',
  RESOLVED: 'A correção definitiva subiu. O registro fica pelo histórico, mas não é mais oferecido.',
  RETIRED: 'Deixou de valer — o serviço saiu do ar, o componente foi trocado, o cenário não existe mais.',
};

export interface ReferenciaDeChamado {
  id: string;
  number: string;
  subject: string;
}

/**
 * A linha de `KnownError` como o banco a guarda.
 *
 * É o que `POST /known-errors` e `PATCH /known-errors/:id` devolvem — sem
 * `_count` e sem `problemTicket`, que só a listagem e a ficha incluem.
 */
export interface LinhaDeErroConhecido {
  id: string;
  problemTicketId: string;
  code: string;
  title: string;
  symptoms: string;
  rootCause: string | null;
  workaround: string;
  affectedServiceIds: string[];
  affectedCiIds: string[];
  status: SituacaoDeErro;
  permanentFixTicketId: string | null;
  knowledgeArticleId: string | null;
  /** Mantido por trigger no banco — a aplicação nunca o incrementa. */
  occurrenceCount: number;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** `GET /known-errors` — a linha com o problema de origem e a contagem. */
export interface ErroConhecidoListado extends LinhaDeErroConhecido {
  problemTicket: ReferenciaDeChamado;
  _count: { incidents: number };
}

export interface IncidenteVinculado {
  knownErrorId: string;
  ticketId: string;
  /** `false` guarda que a Central sugeriu e o operador **não** usou. */
  applied: boolean;
  appliedById: string | null;
  appliedAt: string;
  note: string | null;
  ticket: { id: string; number: string; subject: string; openedAt: string };
}

/** `GET /known-errors/:id`. */
export interface ErroConhecido extends LinhaDeErroConhecido {
  problemTicket: ReferenciaDeChamado;
  permanentFixTicket: ReferenciaDeChamado | null;
  incidents: IncidenteVinculado[];
}

export type MotivoDaSugestao = 'TEXTO' | 'MESMO_IC' | 'MESMO_SERVICO' | 'RECORRENCIA';

export const ROTULO_DE_MOTIVO: Record<MotivoDaSugestao, string> = {
  TEXTO: 'o relato se parece',
  MESMO_IC: 'mesmo item de configuração',
  MESMO_SERVICO: 'mesmo serviço',
  RECORRENCIA: 'já aconteceu antes',
};

/** `GET /tickets/:id/known-errors/suggestions` — o critério 4.15. */
export interface SugestaoDeErro {
  id: string;
  code: string;
  title: string;
  /** 0 a 100. A escala vem do servidor; a tela não a reinventa. */
  score: number;
  reasons: MotivoDaSugestao[];
  workaround: string;
  symptoms: string;
  occurrenceCount: number;
  /** Já registrado neste chamado — sugerido e decidido, num sentido ou noutro. */
  alreadyLinked: boolean;
}

/** O que `POST /known-errors/:id/apply` devolve. */
export interface ResultadoDaAplicacao {
  link: IncidenteVinculado;
  /** Relido do banco **depois** da trigger. */
  occurrenceCount: number;
}
